// class for handling routing on gtm and populating gtm with user information
import {IdcMessagePubSubService, IdcMfeMessage, IdcMfeMessageTypes, UserDetailsData} from "@idc-message/idc-message-pub-sub";
import {runAfterDOMLoaded} from "../helpers/document-helper";
import {environment as env} from "../environments/environment";

export class GtmHandler {

    public static loadGtmScript() {
        this.embedGtmContainer();
        this.updateGtmOnUrlChanges();
    }

    public static runListeners() {
        runAfterDOMLoaded(() => this.listenToMessages());
    }

    /**
     * Adds Google Tag Manager code to DOM and runs the script to load/initialize GTM.
     * @private
     */
    private static embedGtmContainer() {
        // prepare gtm parameters
        let gtmParameters = '';
        if (env.googleTagManagerEnvironmentAuth && env.googleTagManagerEnvironment) {
            gtmParameters = `&gtm_auth=${env.googleTagManagerEnvironmentAuth}&gtm_preview=${env.googleTagManagerEnvironment}`;
        }

        // call the original javascript function from Google Tag Manager
        this.addGtmToDom(window, document, 'script', 'dataLayer', env.googleTagManagerContainer, gtmParameters);
    }

    /**
     * Original javascript function from Google Tag Manager, only formatted.
     * "gtmParameters" was added to avoid empty parameters in the url.
     */
    // @ts-expect-error: original javascript
    private static addGtmToDom(w, d, s, l, i, gtmParameters: string) {
        w[l] = w[l] || [];
        w[l].push({'gtm.start': new Date().getTime(), event: 'gtm.js'});

        const f = d.getElementsByTagName(s)[0],
            j = d.createElement(s), dl = l != 'dataLayer' ? '&l=' + l : '';
        j.async = true;
        j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl + gtmParameters;
        f.parentNode.insertBefore(j, f);
    }

    /**
     * Add User_ID to GTM in order to identify current user.
     * @param message IdcMfeMessage containing user details
     * @private
     */
    private static populateUserId(message: IdcMfeMessage): void {
        function hasUserIdInDataLayer() {
            // It's enough to check for just User_ID as we are pushing all variables at once
            return window["dataLayer"].find((obj: object) => Object.keys(obj).indexOf('User_ID') !== -1);
        }

        if (message.name === IdcMfeMessageTypes.UserData && !hasUserIdInDataLayer()) {
            const userDetails = message.data as UserDetailsData;
            const userId = userDetails.userDetails.id;

            window["dataLayer"].push({
                "User_ID": userId,
                "region": "N/A",
                "topic": "N/A",
                "verticalMarket": "N/A",
                "event": "datalayer_loaded"
            });
        }
    }

    /**
     * Listen to messages from Authorization fragment in order to get user ID.
     * @private
     */
    private static listenToMessages() {
        const msgService = new IdcMessagePubSubService();
        msgService.runListeners()
            .then((message: IdcMfeMessage) => {
                this.populateUserId(message);
            })
            .catch(() => {
                // Promise rejected - not IDC message
            })
            .finally(() => {
                this.runListeners();
            })
    }

    /**
     * SPAs are not reloading the page when navigating, so we need to send navigation events to GTM manually.
     * We are proxying window history events that are triggered by the core apps and submitting the url changes
     * to GTM.
     * We cannot use the popstate_event, because calling history.pushState() or replaceState() won't trigger it.
     * @private
     */
    private static proxyWindowHistory(): void {
        window.history.pushState = new Proxy(window.history.pushState, {
            // eslint-disable-next-line
            apply: (target, thisArg, argArray: [data: any, unused: string, url?: string | URL]) => {
                this.submitNavigationEventToGtm();
                return target.apply(thisArg, argArray);
            },
        });

        window.history.replaceState = new Proxy(window.history.replaceState, {
            // eslint-disable-next-line
            apply: (target, thisArg, argArray: [data: any, unused: string, url?: string | URL]) => {
                this.submitNavigationEventToGtm();
                return target.apply(thisArg, argArray);
            },
        });
    }

    private static updateGtmOnUrlChanges() {
        this.submitNavigationEventToGtm(); // initial page load navigation event
        this.proxyWindowHistory();
    }

    /**
     * Creates new navigation event and submits it to Google Tag Manager.
     * @private
     */
    private static submitNavigationEventToGtm() {
        const gtmTag = {
            event: 'page',
            pageName: window.location.pathname + window.location.search
        };
        window.dataLayer.push(gtmTag);
    }
}
